import { hasValue, isNullish } from "./common-util";
import { DatetimeValue } from "../types";

/**
 * 年月から文字列を作成して返却する。
 *
 * @param value 年月
 * @param separator 区切り文字
 */
export function ymToText(
  {
    year,
    month,
  }: {
    year: number;
    month: number;
  },
  separator = "-",
) {
  return (
    year.toString(10).padStart(4, "0") +
    separator +
    month.toString(10).padStart(2, "0")
  );
}
/**
 * 年月からDateを作成して返却する。
 */
export function ymToDate(value: { year: number; month: number }) {
  return new Date(value.year, value.month - 1);
}

/**
 * 年月日の情報から日付の文字列を作成して返却する。
 */
export function ymdToText(
  {
    year,
    month,
    day,
  }: {
    year: number;
    month: number;
    day: number;
  },
  separator: string = "-",
) {
  return (
    year.toString(10).padStart(4, "0") +
    separator +
    month.toString(10).padStart(2, "0") +
    separator +
    day.toString(10).padStart(2, "0")
  );
}

/**
 * 時間の情報から日付の文字列を作成して返却する。
 *
 * @param hour 時
 * @param minute 分
 * @param separator 区切り文字
 */
export function createTimeStr(
  hour: number,
  minute: number,
  separator: string = ":",
) {
  return (
    String(hour).padStart(2, "0") + separator + String(minute).padStart(2, "0")
  );
}

const timeTextRegex = /^(\d\d):(\d\d)$/;

// /**
//  * テキストが時分（hh:mm）の形式であればtrueを返す。
//  */
// export function isTimeText(text: string): boolean {
//   const match = text.match(timeTextRegex);
//   if (isNullish(match)) return false;
//
//   const hour = Number.parseInt(match[1], 10);
//   // 一応24時も許す。
//   if (isNaN(hour) || hour < 0 || 24 < hour) return false;
//
//   const minute = Number.parseInt(match[2], 10);
//   if (isNaN(minute) || minute < 0 || 59 < minute) return false;
//
//   return true;
// }

/**
 * テキストが時分（hh:mm）の形式であればオブジェクトに変換して返す。
 * 変換できなければnullを返す。
 */
export function timeTextToTimeOrNull(
  text: string,
): { hour: number; minute: number } | null {
  const match = text.trim().match(timeTextRegex);
  if (isNullish(match)) return null;

  const hour = Number.parseInt(match[1], 10);
  // 一応24時も許す。
  if (isNaN(hour) || hour < 0 || 24 < hour) return null;

  const minute = Number.parseInt(match[2], 10);
  if (isNaN(minute) || minute < 0 || 59 < minute) return null;

  return { hour, minute };
}

export function timeToTimeText(time: { hour: number; minute: number }): string {
  const hourText = time.hour.toString(10).padStart(2, "0");
  const minuteText = time.minute.toString(10).padStart(2, "0");
  return `${hourText}:${minuteText}`;
}

/**
 * 年月の情報からDateを作成して返却する。
 *
 * @param year 年
 * @param month 月
 */
export function createYearMonthDate(year: number, month: number) {
  // 年月指定のみでDateが作成できないため、引数で受け取った年月の1日を指定する。
  return createDate(year, month, 1);
}

export function createDateFromYmd(
  ymd: { year: number; month: number; day: number } | null | undefined,
): Date | null {
  if (isNullish(ymd)) return null;
  return createDate(ymd.year, ymd.month, ymd.day);
}

/**
 * 年月日の情報からDateを作成して返却する。
 *
 * @param year 年
 * @param month 月
 * @param day 日
 */
export function createDate(year: number, month: number, day: number) {
  try {
    return new Date(year, month - 1, day);
  } catch (e) {
    return null;
  }
}

/**
 * 年月日を表すテキストからDateを作成して返却する。
 * 変換できない場合はnullを返却する。
 *
 * @param text 年月日を表すテキスト
 * @param separator 区切り文字
 */
export function ymdTextToDateOrNull(text: string, separator: string = "-") {
  if (!isDateText(text, separator)) return null;
  const [year, month, day] = text.split(separator).map(Number);
  return createDate(year, month, day);
}

/**
 * 年月日を表すテキストからymdを作成して返却する。
 * 変換できない場合はnullを返却する。
 *
 * @param text 年月日を表すテキスト
 * @param separator 区切り文字
 */
export function ymdTextToYmdOrNull(text: string, separator: string = "-") {
  if (!isDateText(text, separator)) return null;
  const [year, month, day] = text.split(separator).map(Number);
  return { year, month, day };
}

/**
 * yyyy-MM形式の日付文字列を受け取り、日時として利用できるか判定する。
 * 区切り文字文字を変更する場合は引数を設定すること。
 *
 * @param strDate 日付文字列
 * @param separator 区切り文字
 */
export function isYearMonthText(strDate: string, separator: string = "-") {
  // 空文字は許容しない
  if (strDate === "") {
    return false;
  }

  // yyyy-MM形式
  const regExp = "^\\d{4}" + "\\" + separator + "\\d{1,2}$";
  if (!strDate.match(regExp)) {
    return false;
  }

  // 日付変換された日付が元の入力値と同じ事を確認
  const [year, month] = strDate.split(separator).map(Number);

  const date = new Date(strDate + separator + String(1));
  const [dYear, dMonth] = getDateYmd(date);

  return dYear === year && dMonth === month;
}

/**
 * yyyy-MM-dd形式の日付文字列を受け取り、日時として利用できるか判定する。
 * @param strDate 日付文字列
 * @param separator 区切り文字
 */
export function isDateText(strDate: string, separator: string = "-") {
  // 空文字は許容しない
  if (strDate === "") {
    return false;
  }

  // yyyy-MM-dd形式のみ許容
  const regExp =
    "^\\d{4}" + "\\" + separator + "\\d{1,2}" + separator + "\\d{1,2}$";
  if (!strDate.match(regExp)) {
    return false;
  }

  // 日付変換された日付が元の入力値と同じ事を確認
  const [year, month, day] = strDate.split(separator).map(Number);

  const date = new Date(strDate);
  const [dYear, dMonth, dDay] = getDateYmd(date);

  return dYear === year && dMonth === month && dDay === day;
}

/**
 * Dateを年月日に分割して返却。
 * @param date 分割対象の日付
 */
export function getDateYmd(date: Date) {
  const year: number = date.getFullYear();
  const month: number = date.getMonth() + 1;
  const day: number = date.getDate();

  return [year, month, day];
}

/**
 * 日付(年月日)の比較を行う。
 *
 * @param targetDate チェック対象の日時
 * @param baseDate 基準日
 *
 * チェック対象の日時が基準日よりも前の日時の場合:true
 * targetDate < baseDate
 */
export function isBeforeDate(targetDate: Date, baseDate: Date) {
  const [targetYear, targetMonth, targetDay] = getDateYmd(targetDate);
  const [baseYear, baseMonth, baseDay] = getDateYmd(baseDate);

  // 年月日の順に比較を行う。
  // 値が異なる時点で、比較結果を返却する。
  if (targetYear !== baseYear) {
    return targetYear < baseYear;
  } else if (targetMonth !== baseMonth) {
    return targetMonth < baseMonth;
  } else {
    return targetDay < baseDay;
  }
}

/**
 * 日付(年月日)の比較を行う。
 *
 * @param targetDate チェック対象の日時
 * @param baseDate 基準日
 *
 * チェック対象の日時が基準日よりも後の日時の場合:true
 * targetDate > baseDate
 */
export function isAfterDate(targetDate: Date, baseDate: Date) {
  const [targetYear, targetMonth, targetDay] = getDateYmd(targetDate);
  const [baseYear, baseMonth, baseDay] = getDateYmd(baseDate);

  // 年月日の順に比較を行う。
  // 値が異なる時点で、比較結果を返却する。
  if (targetYear !== baseYear) {
    return targetYear > baseYear;
  } else if (targetMonth !== baseMonth) {
    return targetMonth > baseMonth;
  } else {
    return targetDay > baseDay;
  }
}

/**
 * ym型の日付情報を受け取って、文字列を返却する.
 * 日付情報が未定義の場合、ハイフンを返却.
 */
export const ymToTextOrHyphen = (ym?: { year: number; month: number }) => {
  // 日付情報が未定義の場合
  if (isNullish(ym)) {
    return "-";
  }

  return ymToText(ym);
};

/**
 * ymd型の日付情報を受け取って、文字列を返却する.
 * 日付情報が未定義の場合、ハイフンを返却.
 */
export const ymdToTextOrHyphen = (date?: {
  year: number;
  month: number;
  day: number;
}) => {
  // 日付情報が未定義の場合
  if (isNullish(date)) {
    return "-";
  }

  return ymdToText(date);
};

/**
 * 日付(ymd型)を受け取って、文字列を返却する
 * 開始日時と終了日時が設定されている場合、区切り文字で結合した文字列を返却する
 *
 * @param value
 * @param separatorRange
 * @constructor
 */
export function ymdRangeToText(
  value?: {
    start?: {
      year: number;
      month: number;
      day: number;
    } | null;
    end?: {
      year: number;
      month: number;
      day: number;
    } | null;
  },
  separatorRange?: string,
): string {
  if (isNullish(value)) {
    return "";
  }

  const startStr = hasValue(value.start)
    ? ymdToTextOrHyphen(value.start)
    : undefined;

  const endStr = hasValue(value.end) ? ymdToTextOrHyphen(value.end) : undefined;

  if (hasValue(startStr) && hasValue(endStr)) {
    return startStr + separatorRange + endStr;
  } else if (hasValue(startStr)) {
    return startStr;
  } else if (hasValue(endStr)) {
    return endStr;
  } else {
    return "";
  }
}

export function getLocalNow(): {
  date: {
    year: number;
    month: number;
    day: number;
  };
  time: {
    hour: number;
    minute: number;
  };
} {
  const now = new Date();
  return {
    date: {
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      day: now.getDate(),
    },
    time: {
      hour: now.getHours(),
      minute: now.getMinutes(),
    },
  };
}

/**
 * 2つのymdを比較する。
 * ymdとしての正しさまでは検証しない。
 * @return selfの方が大きければ1, 同じなら0, selfの方が小さければ-1
 */
export function compareYmd(
  self: {
    year: number;
    month: number;
    day: number;
  },
  other: {
    year: number;
    month: number;
    day: number;
  },
): -1 | 0 | 1 {
  if (self.year > other.year) return 1;
  if (self.year < other.year) return -1;

  if (self.month > other.month) return 1;
  if (self.month < other.month) return -1;

  if (self.day > other.day) return 1;
  if (self.day < other.day) return -1;

  return 0;
}

/**
 * 2つのtimeを比較する。
 * timeとしての正しさまでは検証しない。
 * @return selfの方が大きければ1, 同じなら0, selfの方が小さければ-1
 */
export function compareTime(
  self: {
    hour: number;
    minute: number;
  },
  other: {
    hour: number;
    minute: number;
  },
): -1 | 0 | 1 {
  if (self.hour > other.hour) return 1;
  if (self.hour < other.hour) return -1;

  if (self.minute > other.minute) return 1;
  if (self.minute < other.minute) return -1;

  return 0;
}

/**
 * 2つのdatetimeを比較する。
 * datetimeとしての正しさまでは検証しない。
 * @return selfの方が大きければ1, 同じなら0, selfの方が小さければ-1
 */
export function compareDatetime(
  self: DatetimeValue,
  other: DatetimeValue,
): -1 | 0 | 1 {
  const compareYmdResult = compareYmd(self.date, other.date);
  if (compareYmdResult !== 0) return compareYmdResult;

  const compareTimeResult = compareTime(self.time, other.time);
  if (compareTimeResult !== 0) return compareTimeResult;

  return 0;
}

export function datetimeToText({ date, time }: DatetimeValue) {
  return ymdToText(date) + " " + timeToTimeText(time);
}
